import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Empty, Spin } from 'antd';
import { CheckCircleFilled, CommentOutlined, PictureOutlined, ReloadOutlined } from '@ant-design/icons';
import { liveChatApi } from '../../utils/api';

const formatDate = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString('fa-IR', { year: 'numeric', month: 'short', day: 'numeric' }) : '';
};

/** تصویر پست؛ لینک CDN اینستاگرام منقضی می‌شود یا ممکن است بدون پراکسی باز نشود، پس آیکون جایگزین دارد */
const PostThumbnail = ({ src }) => {
  const [failed, setFailed] = useState(false);

  useEffect(() => setFailed(false), [src]);

  if (!src || failed) {
    return (
      <div className="w-full h-full flex items-center justify-center text-2xl text-gray-300 dark:text-gray-600 bg-gray-50 dark:bg-white/5">
        <PictureOutlined />
      </div>
    );
  }
  return <img src={src} alt="" loading="lazy" referrerPolicy="no-referrer" className="w-full h-full object-cover" onError={() => setFailed(true)} />;
};

const PostCard = ({ post, selected, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    aria-pressed={selected}
    className={`relative text-start rounded-lg overflow-hidden border bg-white dark:bg-white/5 cursor-pointer p-0 transition-shadow hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500 ${
      selected ? 'border-primary-500 ring-2 ring-primary-500/40' : 'border-gray-200 dark:border-white/10'
    }`}
  >
    <div className="aspect-square w-full overflow-hidden">
      <PostThumbnail src={post.thumbnail} />
    </div>
    {selected && <CheckCircleFilled className="absolute top-1.5 start-1.5 text-lg text-primary-500 bg-white rounded-full" />}
    <div className="p-2 custom-font">
      <div
        className="text-xs leading-5 text-gray-700 dark:text-gray-200 break-words"
        style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: '2.5rem' }}
      >
        {post.caption || <span className="text-gray-400">بدون متن</span>}
      </div>
      <div className="flex items-center justify-between gap-2 mt-1 text-[11px] text-gray-400">
        <span className="truncate">{formatDate(post.created_at)}</span>
        <span className="shrink-0 flex items-center gap-1">
          <CommentOutlined /> {Number(post.comment_count ?? 0).toLocaleString('fa-IR')}
        </span>
      </div>
    </div>
  </button>
);

/**
 * انتخاب یک پست از پست‌های اخیر حساب اینستاگرام (کنترل فرم: value = شناسه پست).
 * currentPost: پست ذخیره‌شده قانون، که ممکن است دیگر جزو پست‌های اخیر نباشد.
 */
const InstagramPostPicker = ({ channelId, value, onChange, currentPost }) => {
  const [state, setState] = useState({ loading: false, posts: [], error: null });

  const load = useCallback(async () => {
    if (!channelId) return;
    setState({ loading: true, posts: [], error: null });
    try {
      const res = await liveChatApi.getAutoReplyPosts(channelId);
      setState({ loading: false, posts: res?.data ?? [], error: null });
    } catch (error) {
      setState({ loading: false, posts: [], error: error?.message || 'خطا در دریافت پست‌ها' });
    }
  }, [channelId]);

  useEffect(() => {
    load();
  }, [load]);

  const missingCurrent = currentPost?.id && currentPost.id === value && !state.posts.some((p) => p.id === currentPost.id);
  const posts = missingCurrent && !state.loading ? [currentPost, ...state.posts] : state.posts;

  return (
    <div>
      {state.error && (
        <Alert
          type="error"
          showIcon
          className="custom-font mb-2"
          title={state.error}
          action={<Button size="small" icon={<ReloadOutlined />} onClick={load} style={{ fontFamily: 'CustomFont' }}>تلاش دوباره</Button>}
        />
      )}
      <Spin spinning={state.loading}>
        {posts.length === 0 && !state.loading && !state.error ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<span className="custom-font">پستی پیدا نشد</span>} />
        ) : (
          // اسکرول روی یک div ساده: CSS سراسری پروژه overflow هر عنصر grid/flex را با !important غیرفعال می‌کند
          <div className="max-h-72 overflow-y-auto p-0.5" style={{ minHeight: state.loading ? 120 : undefined }}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {posts.map((post) => (
                <PostCard key={post.id} post={post} selected={post.id === value} onSelect={() => onChange?.(post.id)} />
              ))}
            </div>
          </div>
        )}
      </Spin>
    </div>
  );
};

export default InstagramPostPicker;
